// Saipos Service
// Integração com Saipos API para movimentação de estoque
// Cada cliente configura seu próprio token

import { supabase } from '../lib/supabase';

const PROVIDER = 'saipos';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** Erro com código HTTP para expor na UI das tools */
export class SaiposHttpError extends Error {
  constructor(message: string, public readonly httpStatus: number) {
    super(message);
    this.name = 'SaiposHttpError';
  }
}

function extractArrayFromResponse(result: unknown): unknown[] {
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object') {
    const o = result as Record<string, unknown>;
    for (const key of ['data', 'items', 'sales_items', 'records', 'results']) {
      if (Array.isArray(o[key])) return o[key] as unknown[];
    }
  }
  return [];
}

/** Venda associada à movimentação (quando aplicável) */
interface SaiposMovementSale {
  id_sale?: number;
  sale_number?: number;
  [key: string]: any;
}

/** Grupo do ingrediente */
interface SaiposIngredientGroup {
  id_store_group_ingredient?: number;
  desc_store_group_ingredient?: string;
  [key: string]: any;
}

/** Ingrediente na movimentação (inclui current_inventory, average_cost, unidade etc.) */
interface SaiposMovementIngredient {
  id_store?: number;
  average_cost?: number;
  minimium_stock?: number;
  control_inventory?: 'Y' | 'N';
  current_inventory?: number;
  average_cost_method?: string;
  id_store_ingredient?: number;
  include_in_cmv_calc?: 'Y' | 'N';
  desc_store_ingredient?: string;
  ingredient_created_at?: string;
  short_desc_unit_measure?: string;
  [key: string]: any;
}

/** Nota fiscal / pedido associado */
interface SaiposIdentificationNf {
  desc_item?: string;
  id_store_provider_nfe_item?: number;
  [key: string]: any;
}

/** Movimentação de estoque (layout API: search_ingredient_movement) */
interface SaiposStockMovement {
  id_store_ingred_movement?: number;
  date_movement?: string;
  notes?: string;
  quantity?: number;
  quantity_entry?: number;
  unit_cost?: number;
  created_at?: string;
  id_movement_type?: number;
  sale?: SaiposMovementSale;
  ingredient?: SaiposMovementIngredient;
  group?: SaiposIngredientGroup;
  identification_nf?: SaiposIdentificationNf;
  [key: string]: any;
}

/** Posição de estoque (derivada de search_ingredient_movement: Ingrediente, Grupo, Estoque atual, Valor unitário) */
interface SaiposStockPosition {
  id_store_ingredient?: number;
  current_inventory?: number;
  current_stock?: number;
  average_cost?: number;
  short_desc_unit_measure?: string;
  desc_store_ingredient?: string;
  desc_store_group_ingredient?: string;
  ingredient?: SaiposMovementIngredient;
  group?: SaiposIngredientGroup;
  [key: string]: any;
}

interface SaiposSale {
  id?: string;
  store_id?: string;
  sale_number?: string;
  sale_date?: string;
  customer_name?: string;
  customer_document?: string;
  total?: number;
  /** Valor da venda (campo correto na API Saipos) */
  payment_amount?: number;
  /** Tipo da venda: 1=Entrega, 2=Retirada no balcão, 3=Salão, 4=Ficha/Senha */
  id_sale_type?: number;
  status?: string;
  payment_method?: string;
  [key: string]: any;
}

interface SaiposSaleItem {
  id?: string;
  sale_id?: string;
  product_id?: string;
  product_name?: string;
  desc_sale_item?: string;
  quantity?: number;
  quantidade?: number;
  unit_price?: number;
  valor?: number;
  total?: number;
  [key: string]: any;
}

interface SaiposSaleStatusHistory {
  id?: string;
  sale_id?: string;
  status?: string;
  status_date?: string;
  user?: string;
  notes?: string;
  [key: string]: any;
}

interface SaiposFinancialEntry {
  id?: string;
  store_id?: string;
  entry_date?: string;
  entry_type?: string;
  description?: string;
  amount?: number;
  category?: string;
  payment_method?: string;
  [key: string]: any;
}

interface SaiposConfig {
  token: string;
  storeId?: string;
}

class SaiposService {
  private token: string | null = null;
  private storeId: string | null = null;
  private currentUserId: string | null = null;

  // Carregar configuração salva do Supabase
  async loadSavedConfig(userId: string): Promise<SaiposConfig | null> {
    try {
      const { data, error } = await supabase
        .from('user_integrations')
        .select('access_token, provider_user_id')
        .eq('user_id', userId)
        .eq('provider', PROVIDER)
        .eq('is_active', true)
        .single();

      if (error || !data || !data.access_token) {
        console.log('Nenhuma configuração Saipos encontrada');
        return null;
      }

      this.token = data.access_token;
      this.storeId = data.provider_user_id || null;
      this.currentUserId = userId;
      console.log('✅ Configuração do Saipos carregada');
      return {
        token: data.access_token,
        storeId: data.provider_user_id || undefined
      };
    } catch (error) {
      console.error('Erro ao carregar configuração Saipos:', error);
      return null;
    }
  }

  // Carregar token salvo do Supabase (método de compatibilidade)
  async loadSavedToken(userId: string): Promise<string | null> {
    const config = await this.loadSavedConfig(userId);
    return config?.token || null;
  }

  // Salvar configuração no Supabase
  async saveConfig(userId: string, token: string, storeId?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_integrations')
        .upsert({
          user_id: userId,
          provider: PROVIDER,
          access_token: token,
          provider_user_id: storeId || null,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,provider'
        });

      if (error) {
        console.error('Erro ao salvar configuração Saipos:', error);
        throw error;
      }

      this.token = token;
      this.storeId = storeId || null;
      this.currentUserId = userId;
      console.log('✅ Configuração do Saipos salva');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      throw error;
    }
  }

  // Salvar token no Supabase (método de compatibilidade)
  async saveToken(userId: string, token: string): Promise<void> {
    await this.saveConfig(userId, token);
  }

  // Obter store ID salvo
  async getStoreId(userId: string): Promise<string | null> {
    if (this.storeId && this.currentUserId === userId) {
      return this.storeId;
    }
    const config = await this.loadSavedConfig(userId);
    return config?.storeId || null;
  }

  // Verificar se está conectado
  async isConnected(userId: string): Promise<boolean> {
    if (this.token && this.currentUserId === userId) {
      return true;
    }
    const config = await this.loadSavedConfig(userId);
    return !!config?.token;
  }

  // Obter token (carrega do banco ou retorna o atual)
  async getToken(userId: string): Promise<string | null> {
    if (this.token && this.currentUserId === userId) {
      return this.token;
    }
    return await this.loadSavedToken(userId);
  }

  // Desconectar (remover token)
  async disconnect(userId: string): Promise<void> {
    try {
      await supabase
        .from('user_integrations')
        .delete()
        .eq('user_id', userId)
        .eq('provider', PROVIDER);

      this.token = null;
      this.storeId = null;
      this.currentUserId = null;
      console.log('✅ Saipos desconectado');
    } catch (error) {
      console.error('Erro ao desconectar Saipos:', error);
      throw error;
    }
  }

  /** Timeout em ms para requisições ao proxy/API Saipos (evita loading infinito). */
  private static readonly REQUEST_TIMEOUT_MS = 120_000;
  /** Delay antes de retentar quando a API Saipos retorna erro de connection pool (ms). */
  private static readonly RETRY_DELAY_MS = 5_000;
  /** Número máximo de retentativas em caso de connection pool. */
  private static readonly MAX_POOL_RETRIES = 2;

  private static isConnectionPoolError(message: string): boolean {
    const lower = message.toLowerCase();
    return lower.includes('connection pool') || lower.includes('timed out acquiring');
  }

  // Fazer requisição autenticada à API do Saipos via Edge Function (evita CORS). Retorna { data, status }.
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<{ data: T; status: number }> {
    if (!this.token) {
      throw new Error('Token Saipos não configurado. Configure o token primeiro.');
    }

    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Configuração do Supabase não encontrada');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SaiposService.REQUEST_TIMEOUT_MS);

    const proxyUrl = `${SUPABASE_URL}/functions/v1/saipos-proxy`;
    
    console.log('🍽️ [Saipos] Chamando Edge Function:', {
      url: proxyUrl,
      endpoint,
      hasToken: !!this.token,
      method: options.method || 'GET',
      retryCount
    });
    
    let response: Response;
    try {
      response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          endpoint,
          token: this.token,
          method: options.method || 'GET',
          body: options.body ? JSON.parse(options.body as string) : undefined
        }),
        signal: controller.signal
      });
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') {
        throw new Error('A requisição ao Saipos demorou muito e foi cancelada. Tente novamente.');
      }
      throw err;
    }
    clearTimeout(timeoutId);

    console.log('🍽️ [Saipos] Resposta da Edge Function:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [Saipos] Erro na resposta:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      
      let errorMessage = `Erro na API Saipos: ${response.statusText}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      // Retry até MAX_POOL_RETRIES vezes se for erro de connection pool do lado Saipos
      if (retryCount < SaiposService.MAX_POOL_RETRIES && SaiposService.isConnectionPoolError(errorMessage)) {
        console.log(`🔄 [Saipos] Erro de connection pool (tentativa ${retryCount + 1}/${SaiposService.MAX_POOL_RETRIES}), aguardando ${SaiposService.RETRY_DELAY_MS}ms...`);
        await new Promise(r => setTimeout(r, SaiposService.RETRY_DELAY_MS));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw new SaiposHttpError(errorMessage, response.status);
    }

    const status = response.status;
    const data = await response.json() as T;
    return { data, status };
  }

  /** Chamada à API Order (homolog-order-api.saipos.com) – ex.: cardápio/catálogo. Token opcional. */
  private async makeOrderApiRequest<T>(endpoint: string, token: string | null): Promise<{ data: T; status: number }> {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Configuração do Supabase não encontrada');
    }
    const proxyUrl = `${SUPABASE_URL}/functions/v1/saipos-proxy`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SaiposService.REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          api: 'order',
          endpoint: endpoint.startsWith('/') ? endpoint : `/${endpoint}`,
          token: token || '',
          method: 'GET'
        }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Erro na API Saipos (cardápio): ${response.statusText}`;
        try {
          const err = JSON.parse(errorText);
          errorMessage = err.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new SaiposHttpError(errorMessage, response.status);
      }
      const data = await response.json() as T;
      return { data, status: response.status };
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') {
        throw new Error('A requisição ao Saipos demorou muito e foi cancelada. Tente novamente.');
      }
      throw err;
    }
  }

  /** Consultar cardápio/catálogo (API Order: GET /v2/catalog) */
  async getCatalog(userId: string): Promise<{ data: any; status: number }> {
    const token = await this.getToken(userId);
    return this.makeOrderApiRequest<any>('/v2/catalog', token);
  }

  // Consultar movimentação de estoque (API: GET /v1/search_ingredient_movement)
  // 100% alinhado ao curl: p_date_column_filter=date_movement&p_filter_date_start&p_filter_date_end&p_limit&p_offset
  async getStockMovement(
    userId: string,
    filters?: {
      date_from?: string;
      date_to?: string;
      date_column_filter?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: SaiposStockMovement[]; status: number }> {
    const token = await this.getToken(userId);
    if (!token) {
      throw new Error('Token Saipos não configurado');
    }

    const previousToken = this.token;
    this.token = token;

    try {
      // Formato de data como no curl: YYYY-MM-DD HH:mm:ss (API Saipos)
      const year = new Date().getFullYear();
      const defaultStart = `${year - 1}-01-01 00:00:00`;
      const defaultEnd = `${year}-12-31 23:59:59`;
      const dateStart = filters?.date_from
        ? (filters.date_from.includes(' ') || filters.date_from.includes('T')
            ? filters.date_from.replace('T', ' ')
            : `${filters.date_from} 00:00:00`)
        : defaultStart;
      const dateEnd = filters?.date_to
        ? (filters.date_to.includes(' ') || filters.date_to.includes('T')
            ? filters.date_to.replace('T', ' ')
            : `${filters.date_to} 23:59:59`)
        : defaultEnd;

      const params = new URLSearchParams();
      params.append('p_date_column_filter', filters?.date_column_filter ?? 'date_movement');
      params.append('p_filter_date_start', dateStart);
      params.append('p_filter_date_end', dateEnd);
      params.append('p_limit', String(filters?.limit ?? 300));
      params.append('p_offset', String(filters?.offset ?? 0));

      const queryString = params.toString();
      const endpoint = `/search_ingredient_movement?${queryString}`;

      const { data: raw, status } = await this.makeRequest<{ data?: SaiposStockMovement[] } | SaiposStockMovement[]>(endpoint);
      let list: SaiposStockMovement[];
      if (Array.isArray(raw)) list = raw;
      else if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) list = (raw as any).data;
      else list = [];
      return { data: list, status };
    } finally {
      this.token = previousToken;
    }
  }

  // Consultar posição de estoque (derivada das Movimentações de Estoque - search_ingredient_movement)
  // API Saipos: intervalo de datas não pode ser superior a 15 dias. Usamos últimos 15 dias para obter posição atual.
  async getStockPosition(
    userId: string,
    filters?: {
      store_id?: string;
      ingredient_id?: string;
      limit?: number;
      offset?: number;
      /** Últimos N dias (máx. 15 por limite da API). Padrão 15. */
      days?: number;
    }
  ): Promise<{ data: SaiposStockPosition[]; status: number }> {
    const now = new Date();
    const days = Math.min(Math.max(1, filters?.days ?? 15), 15);
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    const dateFrom = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')} 00:00:00`;
    const dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} 23:59:59`;
    const { data: movements, status } = await this.getStockMovement(userId, {
      date_from: dateFrom,
      date_to: dateTo,
      date_column_filter: 'date_movement',
      limit: filters?.limit ?? 2000,
      offset: filters?.offset ?? 0
    });

    if (!movements || movements.length === 0) return { data: [], status };

    const sorted = [...movements].sort((a, b) => {
      const da = a.date_movement || '';
      const db = b.date_movement || '';
      return db.localeCompare(da);
    });
    const byIngredient = new Map<number, SaiposStockMovement>();
    for (const m of sorted) {
      const id = m.ingredient?.id_store_ingredient ?? (m as any).id_store_ingredient;
      if (id == null) continue;
      if (!byIngredient.has(id)) byIngredient.set(id, m);
    }

    const positions: SaiposStockPosition[] = [];
    for (const m of byIngredient.values()) {
      const ing = m.ingredient;
      const group = m.group;
      positions.push({
        id_store_ingredient: ing?.id_store_ingredient,
        current_inventory: ing?.current_inventory ?? (m as any).current_inventory,
        current_stock: ing?.current_inventory ?? (m as any).current_inventory,
        average_cost: ing?.average_cost ?? (m as any).average_cost,
        short_desc_unit_measure: ing?.short_desc_unit_measure,
        desc_store_ingredient: ing?.desc_store_ingredient ?? (m as any).desc_store_ingredient,
        desc_store_group_ingredient: group?.desc_store_group_ingredient ?? (m as any).desc_store_group_ingredient,
        ingredient: ing,
        group
      });
    }

    if (filters?.ingredient_id) {
      const idStr = filters.ingredient_id;
      const filtered = positions.filter(p => String(p.id_store_ingredient ?? p.ingredient?.id_store_ingredient ?? '') === idStr);
      return { data: filtered, status };
    }
    return { data: positions, status };
  }

  // Consultar vendas (API: GET /v1/search_sales)
  // A API exige os 5 parâmetros: p_date_column_filter, p_filter_date_start, p_filter_date_end, p_limit, p_offset.
  // Sem as datas, o backend procura search_sales(p_date_column_filter, p_limit, p_offset) e retorna erro de schema.
  async getSales(
    userId: string,
    filters?: {
      store_id?: string;
      date_from?: string;
      date_to?: string;
      date_column_filter?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: SaiposSale[]; status: number }> {
    const token = await this.getToken(userId);
    if (!token) {
      throw new Error('Token Saipos não configurado');
    }

    const previousToken = this.token;
    this.token = token;

    try {
      // Intervalo padrão: último ano (API exige p_filter_date_start e p_filter_date_end)
      const year = new Date().getFullYear();
      const defaultStart = `${year - 1}-01-01T00:00:00`;
      const defaultEnd = `${year}-12-31T23:59:59`;
      const dateStart = filters?.date_from
        ? (filters.date_from.includes('T') ? filters.date_from : `${filters.date_from}T00:00:00`)
        : defaultStart;
      const dateEnd = filters?.date_to
        ? (filters.date_to.includes('T') ? filters.date_to : `${filters.date_to}T23:59:59`)
        : defaultEnd;

      const params = new URLSearchParams();
      params.append('p_date_column_filter', filters?.date_column_filter ?? 'shift_date');
      params.append('p_filter_date_start', dateStart);
      params.append('p_filter_date_end', dateEnd);
      params.append('p_limit', String(filters?.limit ?? 300));
      params.append('p_offset', String(filters?.offset ?? 0));

      const queryString = params.toString();
      const endpoint = `/search_sales?${queryString}`;

      const { data: raw, status } = await this.makeRequest<{ data?: SaiposSale[] } | SaiposSale[]>(endpoint);
      let list: SaiposSale[];
      if (Array.isArray(raw)) list = raw;
      else if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) list = (raw as any).data;
      else list = [];
      return { data: list, status };
    } finally {
      this.token = previousToken;
    }
  }

  // Consultar itens de venda (API: GET /v1/sales_items)
  // Mesmo padrão do curl: p_date_column_filter, p_filter_date_start, p_filter_date_end, p_limit, p_offset
  async getSaleItems(
    userId: string,
    filters?: {
      date_from?: string;
      date_to?: string;
      date_column_filter?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: SaiposSaleItem[]; status: number }> {
    const token = await this.getToken(userId);
    if (!token) {
      throw new Error('Token Saipos não configurado');
    }

    const previousToken = this.token;
    this.token = token;

    try {
      const year = new Date().getFullYear();
      const defaultStart = `${year - 1}-01-01 00:00:00`;
      const defaultEnd = `${year}-12-31 23:59:59`;
      const dateStart = filters?.date_from
        ? (filters.date_from.includes(' ') || filters.date_from.includes('T')
            ? filters.date_from.replace('T', ' ')
            : `${filters.date_from} 00:00:00`)
        : defaultStart;
      const dateEnd = filters?.date_to
        ? (filters.date_to.includes(' ') || filters.date_to.includes('T')
            ? filters.date_to.replace('T', ' ')
            : `${filters.date_to} 23:59:59`)
        : defaultEnd;

      const params = new URLSearchParams();
      params.append('p_date_column_filter', filters?.date_column_filter ?? 'shift_date');
      params.append('p_filter_date_start', dateStart);
      params.append('p_filter_date_end', dateEnd);
      params.append('p_limit', String(filters?.limit ?? 300));
      params.append('p_offset', String(filters?.offset ?? 0));

      const queryString = params.toString();
      const endpoint = `/sales_items?${queryString}`;

      const { data: raw, status } = await this.makeRequest<Record<string, unknown> | SaiposSaleItem[]>(endpoint);
      const list = extractArrayFromResponse(raw);
      if (list.length > 0 && typeof list[0] === 'object' && list[0] !== null) {
        console.log('🍽️ [Saipos] sales_items amostra (1º item):', JSON.stringify(list[0]));
      }
      return { data: list as SaiposSaleItem[], status };
    } finally {
      this.token = previousToken;
    }
  }

  // Consultar histórico de status de venda
  async getSaleStatusHistory(
    userId: string,
    filters?: {
      sale_id?: string;
      store_id?: string;
      status?: string;
      date_from?: string;
      date_to?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: SaiposSaleStatusHistory[]; status: number }> {
    const token = await this.getToken(userId);
    if (!token) {
      throw new Error('Token Saipos não configurado');
    }

    const previousToken = this.token;
    this.token = token;

    try {
      const params = new URLSearchParams();
      
      if (filters?.sale_id) params.append('sale_id', filters.sale_id);
      
      // Usar store_id dos filtros ou o salvo na configuração
      const storeId = filters?.store_id || await this.getStoreId(userId);
      if (storeId) params.append('store_id', storeId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.offset) params.append('offset', String(filters.offset));

      const queryString = params.toString();
      const endpoint = `/search_sale_status_history${queryString ? `?${queryString}` : ''}`;

      const { data: raw, status } = await this.makeRequest<{ data?: SaiposSaleStatusHistory[] } | SaiposSaleStatusHistory[]>(endpoint);
      let list: SaiposSaleStatusHistory[];
      if (Array.isArray(raw)) list = raw;
      else if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) list = (raw as any).data;
      else list = [];
      return { data: list, status };
    } finally {
      this.token = previousToken;
    }
  }

  // Consultar lançamentos financeiros
  async getFinancialEntries(
    userId: string,
    filters?: {
      store_id?: string;
      entry_type?: string;
      date_from?: string;
      date_to?: string;
      category?: string;
      payment_method?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: SaiposFinancialEntry[]; status: number }> {
    const token = await this.getToken(userId);
    if (!token) {
      throw new Error('Token Saipos não configurado');
    }

    const previousToken = this.token;
    this.token = token;

    try {
      const params = new URLSearchParams();
      
      // Usar store_id dos filtros ou o salvo na configuração
      const storeId = filters?.store_id || await this.getStoreId(userId);
      if (storeId) params.append('store_id', storeId);
      if (filters?.entry_type) params.append('entry_type', filters.entry_type);
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.payment_method) params.append('payment_method', filters.payment_method);
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.offset) params.append('offset', String(filters.offset));

      const queryString = params.toString();
      const endpoint = `/search_financial_entry${queryString ? `?${queryString}` : ''}`;

      const { data: raw, status } = await this.makeRequest<{ data?: SaiposFinancialEntry[] } | SaiposFinancialEntry[]>(endpoint);
      let list: SaiposFinancialEntry[];
      if (Array.isArray(raw)) list = raw;
      else if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) list = (raw as any).data;
      else list = [];
      return { data: list, status };
    } finally {
      this.token = previousToken;
    }
  }
}

export const saiposService = new SaiposService();
export type { 
  SaiposStockMovement, 
  SaiposStockPosition,
  SaiposSale,
  SaiposSaleItem,
  SaiposSaleStatusHistory,
  SaiposFinancialEntry
};

